import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from './CartContext';
import { useNavigate, useLocation } from 'react-router-dom';
import './Checkout.css';
import phonepeLogo from '../assets/phonepe.png';
import gpayLogo from '../assets/googlepay.webp';
import paytmLogo from '../assets/paytm.webp';
import { FaArrowLeft } from 'react-icons/fa';

const Checkout = () => {
  const { cartItems, checkoutItems, clearCart, resetCheckout } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  
  // Form states
  const [address, setAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: ''
  });

  const [payment, setPayment] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: ''
  });

  const [upiId, setUpiId] = useState('');

  const [errors, setErrors] = useState({
    address: {},
    payment: {},
    upi: {}
  });

  // New state for order items and total
  const [orderItems, setOrderItems] = useState([]);
  const [orderTotal, setOrderTotal] = useState(0);

  // Function to handle going back
  const handleGoBack = () => {
    // If we have a previous page in history, go back
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      // Otherwise, go to a sensible default (cart page)
      navigate('/cart');
    }
  };

  const resetCheckoutIfNeeded = useCallback(() => {
    if (location.state?.items && checkoutItems.length > 0) {
      resetCheckout();
    }
  }, [location.state, checkoutItems.length, resetCheckout]);

  // Initialize order items and total
  useEffect(() => {
    resetCheckoutIfNeeded();

    let items = [];
    
    if (location.state?.items && Array.isArray(location.state.items)) {
      items = [...location.state.items];
    } 
    else if (Array.isArray(checkoutItems) && checkoutItems.length > 0) {
      items = [...checkoutItems];
    }
    else if (Array.isArray(cartItems)) {
      items = [...cartItems];
    }

    const processedItems = items.map(item => ({
      ...item,
      quantity: Math.max(1, Number(item.quantity) || 1),
      price: Number(item.price) || 0
    }));
    
    if (JSON.stringify(processedItems) !== JSON.stringify(orderItems)) {
      setOrderItems(processedItems);
      setOrderTotal(processedItems.reduce((total, item) => total + (item.price * item.quantity), 0));
    }
  }, [location.state, checkoutItems, cartItems, resetCheckoutIfNeeded, orderItems]);

  // Validate address form
  const validateAddress = () => {
    const newErrors = {};
    if (!address.name.trim()) newErrors.name = 'Name is required';
    if (!address.street.trim()) newErrors.street = 'Street address is required';
    if (!address.city.trim()) newErrors.city = 'City is required';
    if (!address.state.trim()) newErrors.state = 'State is required';
    if (!address.zip.trim()) newErrors.zip = 'ZIP code is required';
    if (!address.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(address.phone)) newErrors.phone = 'Invalid phone number';

    setErrors(prev => ({ ...prev, address: newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  // Validate payment form based on method
  const validatePayment = () => {
    if (paymentMethod === 'card') {
      const newErrors = {};
      if (!payment.cardNumber.trim()) newErrors.cardNumber = 'Card number is required';
      else if (!/^\d{16}$/.test(payment.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Invalid card number';
      }
      
      if (!payment.expiry.trim()) {
        newErrors.expiry = 'Expiry date is required';
      } else {
        const [month, year] = payment.expiry.split('/');
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);
        const currentYear = new Date().getFullYear() % 100;
        const currentMonth = new Date().getMonth() + 1;
        
        if (!/^(0[1-9]|1[0-2])\/([0-9]{2})$/.test(payment.expiry)) {
          newErrors.expiry = 'Invalid format (MM/YY)';
        } else if (monthNum < 1 || monthNum > 12) {
          newErrors.expiry = 'Invalid month (01-12)';
        } else if (yearNum < currentYear || (yearNum === currentYear && monthNum < currentMonth)) {
          newErrors.expiry = 'Card has expired';
        }
      }
      
      if (!payment.cvv.trim()) newErrors.cvv = 'CVV is required';
      else if (!/^\d{3,4}$/.test(payment.cvv)) newErrors.cvv = 'Invalid CVV';
      
      if (!payment.nameOnCard.trim()) newErrors.nameOnCard = 'Name on card is required';
    
      setErrors(prev => ({ ...prev, payment: newErrors }));
      return Object.keys(newErrors).length === 0;
    } else if (paymentMethod === 'upi') {
      // UPI validation
      const newErrors = {};
      if (!upiId.trim()) {
        newErrors.upiId = 'UPI ID is required';
      } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/.test(upiId)) {
        newErrors.upiId = 'Invalid UPI ID (e.g., name@upi)';
      }
      
      setErrors(prev => ({ ...prev, upi: newErrors }));
      return Object.keys(newErrors).length === 0;
    }
    // No validation needed for cash on delivery
    return true;
  };

  // Handle address form submission
  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (validateAddress()) {
      setStep(2);
      setError(null);
    }
  };

  // Handle payment form submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!validatePayment()) return;

    setIsProcessing(true);
    setError(null);

    try {
      // 1. Get JWT token from localStorage
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required. Please login again.');
      }

      // 2. Prepare order data
      const orderData = {
        items: orderItems.map(item => ({
          product: item.id,
          quantity: item.quantity,
        })),
        address: address,
        payment_method: paymentMethod.toUpperCase()
      };

      // 3. Submit order with JWT authentication
      const response = await fetch('http://localhost:8000/api/orders/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData),
      });

      // 4. Handle response
      if (response.status === 401 || response.status === 403) {
        // Clear invalid tokens
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        throw new Error('Your session expired. Please login again.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Order failed');
      }

      // 5. Success
      const result = await response.json();
      clearCart();
      navigate('/order-success', { 
        state: { 
          orderId: result.id,
          amount: orderTotal,
          items: orderItems,
          address,
          paymentMethod 
        }
      });

    } catch (err) {
      console.error('Order error:', err);
      
      if (err.message.includes('session') || err.message.includes('Authentication')) {
        setError('Session expired. Redirecting to login...');
        setTimeout(() => {
          navigate('/login', { state: { from: 'checkout' } });
        }, 2000);
      } else {
        setError(err.message || 'Order processing failed. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // Format card number as user types
  const formatCardNumber = (value) => {
    return value.replace(/\s?/g, '')
      .replace(/(\d{4})/g, '$1 ')
      .trim();
  };

  // Format expiry date as user types
  const formatExpiry = (value) => {
    let cleaned = value.replace(/\D/g, '');
    cleaned = cleaned.slice(0, 4);
    if (cleaned.length > 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  // Render order summary
  const renderOrderSummary = () => (
    <div className="order-summary">
      <h3>Order Summary</h3>
      <div className="order-meta">
        <span>{orderItems.length} {orderItems.length === 1 ? 'item' : 'items'}</span>
        <strong>Total: ₹{orderTotal.toFixed(2)}</strong>
      </div>
    </div>
  );

  // Render payment method selector
  const renderPaymentMethodSelector = () => (
    <div className="payment-method-selector">
      <h3>Payment Method</h3>
      <div className="payment-options">
        <label className={`payment-option ${paymentMethod === 'card' ? 'active' : ''}`}>
          <input
            type="radio"
            name="paymentMethod"
            value="card"
            checked={paymentMethod === 'card'}
            onChange={() => setPaymentMethod('card')}
          />
          <span>Credit/Debit Card</span>
        </label>
        
        <label className={`payment-option ${paymentMethod === 'upi' ? 'active' : ''}`}>
          <input
            type="radio"
            name="paymentMethod"
            value="upi"
            checked={paymentMethod === 'upi'}
            onChange={() => setPaymentMethod('upi')}
          />
          <span>UPI Payment</span>
        </label>

        <label className={`payment-option ${paymentMethod === 'cod' ? 'active' : ''}`}>
          <input
            type="radio"
            name="paymentMethod"
            value="cod"
            checked={paymentMethod === 'cod'}
            onChange={() => setPaymentMethod('cod')}
          />
          <span>Cash on Delivery</span>
        </label>
      </div>
    </div>
  );

  // Render card payment form
  const renderCardPaymentForm = () => (
    <div className="card-payment-form">
      <div className="form-group">
        <label>Card Number</label>
        <input
          type="text"
          value={formatCardNumber(payment.cardNumber)}
          onChange={(e) => setPayment({
            ...payment, 
            cardNumber: e.target.value.replace(/\s/g, '')
          })}
          maxLength="19"
          placeholder="1234 5678 9012 3456"
          className={errors.payment.cardNumber ? 'error' : ''}
        />
        {errors.payment.cardNumber && <span className="field-error">{errors.payment.cardNumber}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Expiry Date</label>
          <input
            type="text"
            value={payment.expiry}
            onChange={(e) => setPayment({
              ...payment, 
              expiry: formatExpiry(e.target.value)
            })}
            maxLength="5"
            placeholder="MM/YY"
            className={errors.payment.expiry ? 'error' : ''}
          />
          {errors.payment.expiry && <span className="field-error">{errors.payment.expiry}</span>}
        </div>

        <div className="form-group">
          <label>CVV</label>
          <input
            type="text"
            value={payment.cvv}
            onChange={(e) => setPayment({
              ...payment, 
              cvv: e.target.value.replace(/\D/g, '')
            })}
            maxLength="4"
            className={errors.payment.cvv ? 'error' : ''}
          />
          {errors.payment.cvv && <span className="field-error">{errors.payment.cvv}</span>}
        </div>
      </div>

      <div className="form-group">
        <label>Name on Card</label>
        <input
          type="text"
          value={payment.nameOnCard}
          onChange={(e) => setPayment({...payment, nameOnCard: e.target.value})}
          className={errors.payment.nameOnCard ? 'error' : ''}
        />
        {errors.payment.nameOnCard && <span className="field-error">{errors.payment.nameOnCard}</span>}
      </div>
    </div>
  );

  // Render UPI payment form
  const renderUPIPaymentForm = () => (
    <div className="upi-payment-form">
      <div className="form-group">
        <label>UPI ID</label>
        <input
          type="text"
          value={upiId}
          onChange={(e) => setUpiId(e.target.value)}
          placeholder="example@upi"
          className={errors.upi.upiId ? 'error' : ''}
        />
      </div>

      <div className="inline-upi-section">
        <p className="upi-label">Pay using any UPI app:</p>
        <div className="upi-apps-inline">
          <button type="button" onClick={() => setUpiId('example@phonepe')}>
            <img src={phonepeLogo} alt="PhonePe" width="24" height="24" />
          </button>
          <button type="button" onClick={() => setUpiId('example@gpay')}>
            <img src={gpayLogo} alt="Google Pay" width="24" height="24" />
          </button>
          <button type="button" onClick={() => setUpiId('example@paytm')}>
            <img src={paytmLogo} alt="Paytm" width="24" height="24" />
          </button>
          <span className="upi-note-inline">or any other UPI app</span>
        </div>
      </div>
    </div>
  );

  // Render Cash on Delivery information
  const renderCashOnDelivery = () => (
    <div className="cod-payment-form">
      <div className="cod-info">
        <p>You'll pay in cash when your order is delivered.</p>
        <p>Please have exact change ready for the delivery person.</p>
      </div>
    </div>
  );

  return (
    <div className="checkout-container">
      <h2>Checkout ({step}/2)</h2>
      
      {error && <div className="error-message">{error}</div>}

      {renderOrderSummary()}

      {step === 1 && (
        <form onSubmit={handleAddressSubmit} className="checkout-form">
          <h3>Shipping Information</h3>
          
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={address.name}
              onChange={(e) => setAddress({...address, name: e.target.value})}
              className={errors.address.name ? 'error' : ''}
            />
            {errors.address.name && <span className="field-error">{errors.address.name}</span>}
          </div>

          <div className="form-group">
            <label>Street Address</label>
            <input
              type="text"
              value={address.street}
              onChange={(e) => setAddress({...address, street: e.target.value})}
              className={errors.address.street ? 'error' : ''}
            />
            {errors.address.street && <span className="field-error">{errors.address.street}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={address.city}
                onChange={(e) => setAddress({...address, city: e.target.value})}
                className={errors.address.city ? 'error' : ''}
              />
              {errors.address.city && <span className="field-error">{errors.address.city}</span>}
            </div>

            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={address.state}
                onChange={(e) => setAddress({...address, state: e.target.value})}
                className={errors.address.state ? 'error' : ''}
              />
              {errors.address.state && <span className="field-error">{errors.address.state}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>ZIP Code</label>
              <input
                type="text"
                value={address.zip}
                onChange={(e) => setAddress({...address, zip: e.target.value})}
                className={errors.address.zip ? 'error' : ''}
              />
              {errors.address.zip && <span className="field-error">{errors.address.zip}</span>}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={address.phone}
                onChange={(e) => setAddress({...address, phone: e.target.value.replace(/\D/g, '')})}
                maxLength="10"
                className={errors.address.phone ? 'error' : ''}
              />
              {errors.address.phone && <span className="field-error">{errors.address.phone}</span>}
            </div>
          </div>

          {/* Add back button for step 1 */}
          <div className="checkout-actions">
            <button 
              type="button" 
              className="back-button"
              onClick={handleGoBack}
            >
              <FaArrowLeft className="mr-1" /> Back
            </button>
            <button type="submit" className="checkout-button">
              Continue to Payment
            </button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={handlePaymentSubmit} className="checkout-form">
          <h3>Payment Information</h3>
          
          {renderPaymentMethodSelector()}
          
          {paymentMethod === 'card' ? renderCardPaymentForm() : 
           paymentMethod === 'upi' ? renderUPIPaymentForm() : 
           renderCashOnDelivery()}

          {/* Keep existing back button for step 2 */}
          <div className="checkout-actions">
            <button 
              type="button" 
              className="back-button"
              onClick={() => setStep(1)}
              disabled={isProcessing}
            >
              <FaArrowLeft className="mr-1" /> Back
            </button>
            <button 
              type="submit" 
              className="checkout-button"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <span className="spinner"></span> Processing...
                </>
              ) : (
                paymentMethod === 'upi' ? 'Pay via UPI' : 
                paymentMethod === 'cod' ? 'Place Order (Cash on Delivery)' : 
                'Complete Purchase'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Checkout;